'use client';
import { postRequest } from '@/api/helpers';
import { routes } from '@/api/routes';
import { isEmpty, validateEmail } from '@/utils/common-helpers';
import { clsx } from 'clsx';
import Head from 'next/head';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import PhoneInput from 'react-phone-input-2';
import { toast } from 'react-toastify';
import CustomAsyncPaginateStatic from './async-paginate-static';

const ContactFormAiml = () => {
    const initialValidation = {
        name: '',
        email: '',
        phone: '',
        message: '',
        attachment: '',
        inquire_for: '',
        budget: '',
        time_frame: '',
    };
    const initialValue = {
        name: '',
        email: '',
        phone: '',
        inquire_for: 'collaboration_opportunity',
        budget: null,
        timeFrame: null,
        message: '',
        attachment: '',
    };

    const data = [
        {
            label: 'Collaboration Opportunity',
            value: 'collaboration_opportunity',
        },
        { label: 'General Inquiry', value: 'general_inquiry' },
    ];

    const budgetData = [
        { label: 'Less than $10,000', value: '1' },
        { label: '$10,000 - $25,000', value: '2' },
        { label: '$25,000 - $50,000', value: '3' },
        { label: '$50,000 - $100,000', value: '4' },
        { label: '$100,000 and above', value: '5' },
        { label: 'Not Sure', value: '6' },
    ];

    const timeFrameData = [
        { label: 'Less than 1 Month', value: '1' },
        { label: '1-3 Months', value: '2' },
        { label: '3-6 Months', value: '3' },
        { label: '6-12 Months', value: '4' },
        { label: '12+ Months', value: '5' },
        { label: 'Not Sure', value: '6' },
    ];

    const { push } = useRouter();

    const { executeRecaptcha } = useGoogleReCaptcha();

    const attachmentRef: any = useRef<string>('');

    const [inquiry, setInquiry] = useState<any>(initialValue);
    const [validation, setValidation] = useState(initialValidation);
    const [submitButtonDisable, setSubmitButtonDisable] = useState<boolean>(false);
    const [fileName, setFileName] = useState<string>('');
    const [phone, setPhone] = useState('');
    const [currentUrl, setCurrentUrl] = useState<string>('');
    const [countryCode, setCountryCode] = useState('');
    const [countryName, setCountryName] = useState('');
    const [selectedData, setSelectedData] = useState({
        label: 'Collaboration Opportunity',
        value: 'collaboration_opportunity',
    });
    const [budget, setBudget] = useState({
        label: 'Select',
        value: '',
    });
    const [timeFrame, setTimeFrame] = useState({
        label: 'Select',
        value: '',
    });

    const handleSubmitForm = useCallback(
        (e: any) => {
            e.preventDefault();
            if (isValidInquiry()) {
                setSubmitButtonDisable(true);

                if (!executeRecaptcha) {
                    setSubmitButtonDisable(false);
                    // Execute recaptcha not yet available - log only in development
                    if (process.env.NODE_ENV === 'development') {
                        console.log('Execute recaptcha not yet available');
                    }
                    setSubmitButtonDisable(false);
                    toast('Execute recaptcha not yet available!', {
                        autoClose: 2000,
                        type: 'error',
                        position: 'bottom-right',
                    });
                    return;
                }
                executeRecaptcha('enquiryFormSubmit')
                    .then(async (gReCaptchaToken: any) => {
                        await submitInquiry(gReCaptchaToken);
                    })
                    .catch((err) => {
                        setSubmitButtonDisable(false);
                        // Log error only in development to avoid information disclosure
                        if (process.env.NODE_ENV === 'development') {
                            console.log('err================', err);
                        }
                    });
            }
        },
        [executeRecaptcha, inquiry],
    );

    const isValidInquiry = () => {
        let isValid = true;
        const updatedValidation = { ...validation };

        if (isEmpty(inquiry.name.trim())) {
            isValid = false;
            updatedValidation['name'] = 'invalid';
        }

        if (isEmpty(inquiry.email) || !validateEmail(inquiry.email)) {
            isValid = false;
            updatedValidation['email'] = 'invalid';
        }

        if (isEmpty(phone.slice(countryCode.length)) || phone.slice(countryCode.length).length < 7) {
            isValid = false;
            updatedValidation['phone'] = 'invalid';
        } else {
            updatedValidation['phone'] = '';
        }

        if (isEmpty(inquiry.inquire_for)) {
            isValid = false;
            updatedValidation['inquire_for'] = 'invalid';
        }

        if (isEmpty(inquiry.message.trim())) {
            isValid = false;
            updatedValidation['message'] = 'invalid';
        }

        if (['apply_for_internship', 'apply_for_job'].includes(selectedData.value)) {
            if (isEmpty(attachmentRef) || isEmpty(attachmentRef.current) || isEmpty(attachmentRef.current.value)) {
                isValid = false;
                updatedValidation['attachment'] = 'invalid';
            }
        }

        if (['get_a_quote'].includes(selectedData.value)) {
            if (isEmpty(budget.value)) {
                isValid = false;
                updatedValidation['budget'] = 'invalid';
            }

            if (isEmpty(timeFrame.value)) {
                isValid = false;
                updatedValidation['time_frame'] = 'invalid';
            }
        }

        setValidation(updatedValidation);
        return isValid;
    };

    const submitInquiry = async (gReCaptchaToken: string) => {
        const inquiryFromData = new FormData();
        inquiryFromData.append('name', inquiry.name);
        inquiryFromData.append('email', inquiry.email);
        inquiryFromData.append('contact_number', phone.slice(countryCode.length));
        inquiryFromData.append('inquire_for', inquiry.inquire_for);

        if (['get_a_quote'].includes(selectedData.value)) {
            if (!isEmpty(inquiry.budget)) {
                inquiryFromData.append('budget', inquiry.budget);
            }

            if (!isEmpty(inquiry.timeFrame)) {
                inquiryFromData.append('time_frame', inquiry.timeFrame);
            }
        }

        inquiryFromData.append('message', inquiry.message);
        inquiryFromData.append('country_code', `+${countryCode}`);
        inquiryFromData.append('country_name', countryName);
        inquiryFromData.append('request_from_page', currentUrl);
        inquiryFromData.append(
            'attachment',
            !isEmpty(attachmentRef.current) && !isEmpty(attachmentRef.current.files[0]) ? attachmentRef.current.files[0] : '',
        );

        const res = await postRequest(routes.CONTACT_INQUIRY_SUBMIT, inquiryFromData, {
            'Content-Type': 'multipart/form-data',
            recaptcha: gReCaptchaToken,
        });

        if (res.status !== 201 || !res.data.status) {
            toast(`${res.data.message}`, {
                autoClose: 2000,
                type: 'error',
                position: 'bottom-right',
            });
        } else {
            clearRef();
            push('/thank-you');
        }
        setSubmitButtonDisable(false);
    };

    const clearRef = () => {
        attachmentRef.current.value = '';
        setPhone(countryCode);
        setValidation({ ...validation, ['email']: '' });
        setInquiry(initialValue);
    };

    const handleEmailValue = (e: any) => {
        const inputValue = e.target.value;
        if (!isEmpty(inputValue) && !validateEmail(inputValue)) {
            setValidation({ ...validation, ['email']: 'invalid' });
        }
    };

    const getCurrentUrl = () => {
        const currentUrl = window.location.href;
        setCurrentUrl(currentUrl);
    };

    useEffect(() => {
        getCurrentUrl();
    }, []);

    return (
        <>
            <Head>
                <title>Contact Inquiry</title>
                <meta property="og:title" content="My page title" key="title" />
            </Head>
            <div className="md:col-span-12 lg:col-span-7 order-responsive-pmigjc">
                <div
                    className="bg-white text-secondary dark:bg-[#3C456133] dark:border-[#ffffff33] dark:border dark:border-solid dark:text-light rounded-lg md:py-5 md:px-[15px] py-5 px-[15px] xl:py-10 xl:px-35 lg:py-5 lg:px-[15px]"
                    data-aos="fade-up"
                    data-aos-duration={1000}
                    data-aos-delay={100}
                >
                    <h2 className="text-blue mb-0">Let&apos;s Solve It Together</h2>
                    <hr className="opacity-10 bg-secondary my-5 dark:bg-[#3C4561] dark:opacity-100" />

                    <form className="grid md:grid-cols-2 gap-x-5 md:gap-x-7 gap-y-5" autoComplete="off">
                        {/* <div> */}
                        <label className="block">
                            <span>Full Name</span>
                            <span className="text-red-600">*</span>
                            <input
                                type="text"
                                value={inquiry.name}
                                autoComplete="off"
                                className={clsx('form-control', validation.name)}
                                onChange={(e) => {
                                    setValidation({ ...validation, ['name']: '' });
                                    setInquiry({ ...inquiry, ['name']: e.target.value });
                                }}
                                placeholder="Type here..."
                            />
                        </label>
                        <label className="block">
                            <span>Email Address</span>
                            <span className="text-red-600">*</span>
                            <input
                                type="text"
                                autoComplete="off"
                                className={clsx('form-control', validation.email)}
                                onChange={(e) => {
                                    setValidation({ ...validation, ['email']: '' });
                                    setInquiry({ ...inquiry, ['email']: e.target.value });
                                }}
                                value={inquiry.email}
                                onBlur={handleEmailValue}
                                placeholder="Type here..."
                            />
                        </label>
                        <div>
                            <label className="block">
                                <span>Mobile Number</span>
                                <span className="text-red-600">*</span>
                            </label>
                            <PhoneInput
                                country={'in'}
                                enableSearch={true}
                                value={phone}
                                countryCodeEditable={false}
                                inputStyle={{
                                    height: 40,
                                    width: '100%',
                                    borderRadius: 10,
                                    borderColor: validation.phone === 'invalid' ? 'red' : '',
                                }}
                                isValid={(value: string, country: any) => {
                                    setCountryCode(country.countryCode);
                                    setCountryName(country.name);

                                    return true;
                                }}
                                onChange={(phoneNumber: any, countryDetails: any) => {
                                    if (countryDetails.dialCode !== countryCode) {
                                        setPhone(countryDetails.dialCode);
                                        setInquiry({ ...inquiry, ['phone']: '' });
                                    } else {
                                        setPhone(phoneNumber);
                                        setInquiry({ ...inquiry, ['phone']: phoneNumber });
                                    }

                                    setValidation({ ...validation, ['phone']: '' });
                                }}
                            />
                        </div>
                        <label className="block">
                            <span>Inquire For</span>
                            <span className="text-red-600">*</span>
                            <CustomAsyncPaginateStatic
                                data={data}
                                selectedData={selectedData}
                                setSelectedData={setSelectedData}
                                handleChange={(e: any) => {
                                    attachmentRef.current.value = '';
                                    setValidation({
                                        ...validation,
                                        ['inquire_for']: '',
                                        ['attachment']: '',
                                        ['message']: '',
                                    });

                                    setInquiry({
                                        ...inquiry,
                                        ['inquire_for']: e.value,
                                        ['attachment']: '',
                                        ['message']: '',
                                    });

                                    if (e.value !== 'get_a_quote') {
                                        setBudget({ label: 'Select', value: '' });
                                        setTimeFrame({ label: 'Select', value: '' });
                                    }
                                }}
                                validation={clsx('rounded-10 mt-1', validation.inquire_for)}
                                placeholder="Select"
                            />
                        </label>
                        {['get_a_quote'].includes(selectedData.value) && (
                            <>
                                <div className="">
                                    <label htmlFor="minmax-range" className="block">
                                        Budget
                                        <span className="text-red-600">*</span>
                                    </label>

                                    <CustomAsyncPaginateStatic
                                        data={budgetData}
                                        selectedData={budget}
                                        setSelectedData={setBudget}
                                        handleChange={(e: any) => {
                                            attachmentRef.current.value = '';
                                            setValidation({
                                                ...validation,
                                                ['budget']: '',
                                            });

                                            setInquiry({
                                                ...inquiry,
                                                ['budget']: e.value,
                                                ['attachment']: '',
                                                ['message']: '',
                                            });
                                        }}
                                        placeholder="Select Budget"
                                        validation={clsx('rounded-10 mt-1 dark:border-[#3C4561]', validation.budget)}
                                    />
                                </div>

                                <div>
                                    <div className="">
                                        <label htmlFor="minmax-range" className="block">
                                            Timeframe
                                            <span className="text-red-600">*</span>
                                        </label>

                                        <CustomAsyncPaginateStatic
                                            data={timeFrameData}
                                            selectedData={timeFrame}
                                            setSelectedData={setTimeFrame}
                                            handleChange={(e: any) => {
                                                attachmentRef.current.value = '';
                                                setValidation({
                                                    ...validation,
                                                    ['time_frame']: '',
                                                });

                                                setInquiry({
                                                    ...inquiry,
                                                    ['time_frame']: e.value,
                                                    ['attachment']: '',
                                                    ['message']: '',
                                                });
                                            }}
                                            validation={clsx('rounded-10 mt-1 dark:border-[#3C4561]', validation.time_frame)}
                                            placeholder="Select Timeframe"
                                            className={clsx('form-control', validation.message)}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        <div className="md:col-span-2">
                            <label className="block">
                                <span>
                                    {['apply_for_internship', 'apply_for_job'].includes(selectedData.value)
                                        ? 'Your Profile Bio / Cover Letter'
                                        : 'Message'}
                                </span>
                                <span className="text-red-600">*</span>
                                <textarea
                                    onChange={(e) => {
                                        setValidation({ ...validation, ['message']: '' });
                                        setInquiry({ ...inquiry, ['message']: e.target.value });
                                    }}
                                    className={clsx('form-control', validation.message)}
                                    rows={3}
                                    value={inquiry.message}
                                    placeholder="Type here..."
                                />
                            </label>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block"></label>
                            <span>
                                {['apply_for_internship', 'apply_for_job'].includes(selectedData.value)
                                    ? 'Attach your Resume'
                                    : 'Attachment (Optional)'}{' '}
                            </span>
                            {['apply_for_internship', 'apply_for_job'].includes(selectedData.value) && (
                                <span className="text-red-600">*</span>
                            )}

                            <div className={clsx('upldimg h-24 mt-1', validation.attachment)}>
                                <label htmlFor="file-upload" className="custom-file-upload mb-0">
                                    {isEmpty(attachmentRef) ||
                                    isEmpty(attachmentRef.current) ||
                                    isEmpty(attachmentRef.current.value)
                                        ? 'Click to select or drop the file here'
                                        : fileName}
                                </label>
                                <input
                                    id="file-upload"
                                    ref={attachmentRef}
                                    type="file"
                                    onChange={(e: any) => {
                                        let allowedExtensions = /(\.jpg|\.jpeg|\.png|\.pdf|\.docx|\.doc|\.txt|\.xlsx|\.webp)$/i;
                                        if (allowedExtensions.exec(e.target.value)) {
                                            if (e.target.files.length > 0) {
                                                setFileName(e.target.files[0].name);
                                            }
                                            setValidation({ ...validation, ['attachment']: '' });
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <hr className="opacity-10 bg-secondary my-0 dark:bg-[#3C4561] dark:opacity-100" />
                        </div>
                        <div className="md:col-span-2">
                            <div className="flex flex-wrap gap-4 items-center">
                                <button
                                    type="button"
                                    className="btn btn-primary cursor-pointer"
                                    aria-labelledby="submitLabel"
                                    onClick={handleSubmitForm}
                                    disabled={submitButtonDisable}
                                >
                                    {submitButtonDisable ? 'Please wait...' : 'Share Your Challenge'}
                                </button>
                                <div>
                                    <p className="text-[#D7DEE5] font-semibold fs-sm mb-0">No obligations • Confidential</p>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default ContactFormAiml;
